import * as React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import type { TaskSummary } from '@bits/shared/zod';
import { cn } from '@/lib/utils';

/**
 * A native <select> renders its list through the OS, so it cannot carry the
 * app's type, radii or spacing. This keeps the trigger looking like the other
 * inputs and draws the list itself.
 */
export function TaskPicker({
  tasks,
  onSelect,
  placeholder = 'Add a task you worked on…',
  className,
}: {
  tasks: readonly TaskSummary[];
  onSelect: (taskId: string) => void;
  placeholder?: string;
  className?: string;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger
        className={cn(
          'flex h-11 w-full items-center justify-between rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-4 text-sm text-[hsl(var(--muted-foreground))] transition-colors hover:border-[hsl(var(--brand))]/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-1 focus-visible:ring-offset-[hsl(var(--background))] disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        disabled={tasks.length === 0}
      >
        <span>{placeholder}</span>
        <ChevronDown
          className={cn('size-4 transition-transform duration-150', open && 'rotate-180')}
        />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-50 max-h-72 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-1.5 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_16px_40px_-12px_rgba(16,24,40,0.22)]"
        >
          {tasks.map((t) => (
            <DropdownMenu.Item
              key={t.id}
              onSelect={() => onSelect(t.id)}
              className="group flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-sm outline-none data-[highlighted]:bg-[hsl(var(--accent))] data-[highlighted]:text-[hsl(var(--accent-foreground))]"
            >
              <Check className="size-4 shrink-0 opacity-0 group-data-[highlighted]:opacity-100" />
              <span className="truncate">{t.title}</span>
              {t.statusKind === 'finished' ? (
                <span className="ml-auto shrink-0 rounded bg-[hsl(var(--muted))] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] group-data-[highlighted]:bg-[hsl(var(--background))]/60">
                  done
                </span>
              ) : null}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
